import React, { useState, useEffect } from 'react';
import {
    Table,
    Switch,
    Button,
    Form,
    Input,
    Select,
    Card,
    Typography,
    Tag,
    InputNumber,
    DatePicker,
    Row,
    Col,
    Flex,
    Space,
    Modal,
    App as AntdApp,
    Popconfirm,
    Divider,
    Checkbox,
    Upload,
    Popover
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useStudents } from '../../hooks/useStudents';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useGroups } from '../../hooks/useGroups';
import { usePackagePlans } from '../../hooks/usePackagePlans';
import { addStudent, updateStudent, deleteStudent } from '../../store/slices/studentsSlice';
import { updateStudentPhoto } from '../../store/slices/studentsSlice';
import dayjs from 'dayjs';
import InputMask from 'react-input-mask';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Kolon görünürlüğü sabitleri (bileşen dışında sabit referans)
const STORAGE_KEY = 'students_table_columns_v1';
const defaultVisibleKeys = [
    'image',
    'fullName',
    'phoneNumber',
    'birthDate',
    'registrationDate',
    'licenseStatus',
    'licenseNumber',
    'ageGroups',
    'groups',
    'status',
    'actions'
];

const ensureHttps = (url) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
};

const getBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
});

const Students = () => {
    const { message } = AntdApp.useApp();
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { students, loading, refetchStudents } = useStudents(selectedBranch?.id, selectedPlace?.id);
    const { ageGroups } = useAgeGroups(selectedBranch?.id, selectedPlace?.id);
    const { groups } = useGroups(selectedBranch?.id, selectedPlace?.id);
    const { packagePlans } = usePackagePlans(selectedBranch?.id, selectedPlace?.id);
    
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [form] = Form.useForm();
    const [showPackageSection, setShowPackageSection] = useState(false);
    const [studentImageFile, setStudentImageFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    
    // Filtreleme state'leri
    const [searchText, setSearchText] = useState('');
    const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [showInactiveStudents, setShowInactiveStudents] = useState(false);
    
    // Pagination state'i
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // Kolon görünürlüğü state ve persistence
    const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return defaultVisibleKeys;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            return defaultVisibleKeys;
        } catch (e) {
            return defaultVisibleKeys;
        }
    });

    useEffect(() => {
        try {
            // En az bir kolon görünür kalsın
            const ensured = visibleColumnKeys.length === 0 ? defaultVisibleKeys : visibleColumnKeys;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(ensured));
        } catch (e) {
            // ignore
        }
    }, [visibleColumnKeys]);

    const handleToggleAllColumns = (checked) => {
        setVisibleColumnKeys(checked ? defaultVisibleKeys : ['fullName']);
    };

    const renderColumnToggleContent = () => {
        const options = [
            { label: 'Resim', value: 'image' },
            { label: 'Öğrenci Adı', value: 'fullName' },
            { label: 'Telefon', value: 'phoneNumber' },
            { label: 'Doğum Tarihi', value: 'birthDate' },
            { label: 'Kayıt Tarihi', value: 'registrationDate' },
            { label: 'Lisans Durumu', value: 'licenseStatus' },
            { label: 'Lisans No', value: 'licenseNumber' },
            { label: 'Yaş Grupları', value: 'ageGroups' },
            { label: 'Gruplar', value: 'groups' },
            { label: 'Durum', value: 'status' },
            { label: 'İşlemler', value: 'actions' },
        ];
        return (
            <div style={{ maxWidth: 240 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text strong>Kolonlar</Text>
                    <Switch
                        size="small"
                        checked={visibleColumnKeys.length === defaultVisibleKeys.length}
                        onChange={handleToggleAllColumns}
                    />
                </div>
                <Checkbox.Group
                    value={visibleColumnKeys}
                    onChange={(keys) => {
                        // En az bir kolon görünür olsun
                        if (keys.length === 0) return;
                        setVisibleColumnKeys(keys);
                    }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                        {options.map(opt => (
                            <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
                <div style={{ marginTop: 8 }}>
                    <Button size="small" onClick={() => setVisibleColumnKeys(defaultVisibleKeys)}>Varsayılana Sıfırla</Button>
                </div>
            </div>
        );
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        const limited = newFileList.slice(-1);
        setFileList(limited);
        const fileObj = limited[0]?.originFileObj;
        setStudentImageFile(fileObj || null);
    };

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
        setPreviewTitle(file.name || 'Önizleme');
    };

    // Pagination değişikliklerini handle et
    const handleTableChange = (paginationInfo) => {
        setPagination((prev) => {
            const nextCurrent = paginationInfo.current;
            const nextPageSize = paginationInfo.pageSize;
            if (prev.current === nextCurrent && prev.pageSize === nextPageSize) {
                return prev;
            }
            return {
                ...prev,
                current: nextCurrent,
                pageSize: nextPageSize,
            };
        });
    };



    // Hızlı tarih seçimi fonksiyonları
    const setQuickDateRange = (type) => {
        const now = dayjs();
        let startTime, endTime;

        switch (type) {
            case 'thisMonth':
                startTime = now; // Bugünden başla
                endTime = now.endOf('month'); // Ayın sonuna kadar
                break;
            case 'oneMonth':
                startTime = now;
                endTime = now.add(1, 'month');
                break;
            case 'threeMonths':
                startTime = now;
                endTime = now.add(3, 'months');
                break;
            case 'sixMonths':
                startTime = now;
                endTime = now.add(6, 'months');
                break;
            case 'oneYear':
                startTime = now;
                endTime = now.add(1, 'year');
                break;
            default:
                startTime = now;
                endTime = now.add(1, 'month');
        }

        form.setFieldsValue({
            packageStartTime: startTime,
            packageEndTime: endTime
        });
    };

    const handleAddStudent = async (values) => {
        try {
            if (!selectedBranch?.id || !selectedPlace?.id) {
                message.error('Lütfen önce branş ve şube seçin.');
                return;
            }

            // Eğer paket planı seçildiyse ve fiyat boş bırakıldıysa, plan fiyatını kullan
            let finalPackagePrice = values.packagePrice;
            if (values.packagePlanId && !values.packagePrice) {
                const selectedPackagePlan = packagePlans.find(plan => plan.id === values.packagePlanId);
                finalPackagePrice = selectedPackagePlan?.price || 0;
            }

            // Telefon numarasını maskesiz ve başında 0 olmadan al
            let phoneNumber = values.phoneNumber ? values.phoneNumber.replace(/\D/g, '') : '';
            if (phoneNumber.startsWith('0')) phoneNumber = phoneNumber.substring(1);
            phoneNumber = phoneNumber.substring(0, 10);

            // Anne/Baba telefonları için de aynı şekilde
            const parents = (values.parents || []).map(parent => ({
                ...parent,
                phoneNumber: parent.phoneNumber ? parent.phoneNumber.replace(/\D/g, '').replace(/^0/, '').substring(0, 10) : ''
            }));

            const studentData = {
                branchId: selectedBranch.id,
                placeId: selectedPlace?.id,
                fullName: values.fullName,
                email: values.email || null,
                birthdate: values.birthdate?.toISOString(),
                gender: values.gender,
                phoneNumber: phoneNumber || null,
                address: values.address || null,
                height: values.height || 0,
                identityNumber: values.identityNumber || null,
                licenseStatus: values.licenseStatus || 0,
                licenseNumber: values.licenseNumber?.trim() || null,
                registrationDate: values.registrationDate?.toISOString() || null,
                groupIds: values.groupIds || [],
                ageGroupIds: values.ageGroupIds || [],
                // Paket bilgileri (opsiyonel)
                packagePlanId: values.packagePlanId || null,
                packagePrice: finalPackagePrice || null,
                paidAmount: values.paidAmount || null,
                packageStartTime: values.packageStartTime?.toDate() || null,
                packageEndTime: values.packageEndTime?.toDate() || null,
                packageNote: values.packageNote || null,
                parents: parents,
                studentImageFile: studentImageFile || undefined
            };

            await dispatch(addStudent(studentData)).unwrap();
            message.success('Öğrenci başarıyla eklendi!');
            refetchStudents();
            form.resetFields();
            setShowAddForm(false);
            setShowPackageSection(false);
            setStudentImageFile(null);
            setFileList([]);
        } catch (error) {
            console.error('Add student error:', error);
            message.error(`Öğrenci eklenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleEditStudent = async (values) => {
        try {
            let phoneNumber = values.phoneNumber ? values.phoneNumber.replace(/\D/g, '') : '';
            if (phoneNumber.startsWith('0')) phoneNumber = phoneNumber.substring(1);
            phoneNumber = phoneNumber.substring(0, 10);
            const parents = (values.parents || []).map(parent => ({
                ...parent,
                phoneNumber: parent.phoneNumber ? parent.phoneNumber.replace(/\D/g, '').replace(/^0/, '').substring(0, 10) : ''
            }));
            const studentData = {
                id: editingStudent.id,
                fullName: values.fullName,
                email: values.email || null,
                birthdate: values.birthdate?.toISOString(),
                gender: values.gender,
                phoneNumber: phoneNumber || null,
                address: values.address || null,
                height: values.height || 0,
                identityNumber: values.identityNumber || null,
                licenseStatus: values.licenseStatus || 0,
                // Boş gönderilirse API lisans numarasını temizler; null gönderilirse mevcut değer korunur.
                licenseNumber: (values.licenseNumber ?? '').trim(),
                registrationDate: values.registrationDate?.toISOString() || null,
                groupIds: values.groupIds || [],
                ageGroupIds: values.ageGroupIds || [],
                packagePlanIds: values.packagePlanIds || [],
                branchIds: [selectedBranch.id],
                parents: parents,
                status: editingStudent.status,
                // Fotoğraf ayrı servise gönderilecek
            };

            await dispatch(updateStudent(studentData)).unwrap();
            if (studentImageFile) {
                await dispatch(updateStudentPhoto({ id: editingStudent.id, studentImageFile })).unwrap();
            }
            message.success('Öğrenci başarıyla güncellendi!');
            refetchStudents();
            form.resetFields();
            setShowAddForm(false);
            setEditingStudent(null);
            setStudentImageFile(null);
            setFileList([]);
        } catch (error) {
            console.error('Update student error:', error);
            message.error(`Öğrenci güncellenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleDeleteStudent = async (id) => {
        try {
            await dispatch(deleteStudent(id)).unwrap();
            message.success('Öğrenci başarıyla silindi!');
            refetchStudents();
        } catch (error) {
            console.error('Delete student error:', error);
            message.error(`Öğrenci silinirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleStatusChange = async (studentId, newStatus) => {
        try {
            const student = students.find(m => m.id === studentId);
            if (!student) return;

            const studentData = {
                id: student.id,
                fullName: student.fullName,
                email: student.email || null,
                birthdate: student.birthDate,
                gender: student.gender,
                phoneNumber: student.phoneNumber || null,
                address: student.address || null,
                height: student.height || 0,
                identityNumber: student.identityNumber || null,
                licenseStatus: student.licenseStatus || null,
                licenseNumber: student.licenseNumber || null,
                registrationDate: student.registrationDate || null,
                groupIds: student.groups ? 
                    groups
                        .filter(group => student.groups.includes(group.name))
                        .map(group => group.id) : [],
                ageGroupIds: student.ageGroups ? 
                    ageGroups
                        .filter(ageGroup => student.ageGroups.includes(ageGroup.name))
                        .map(ageGroup => ageGroup.id) : [],
                packagePlanIds: [],
                branchIds: [selectedBranch.id],
                parents: student.parents || [],
                status: newStatus
            };

            await dispatch(updateStudent(studentData)).unwrap();
            message.success(`Öğrenci durumu ${newStatus ? 'aktif' : 'pasif'} olarak güncellendi!`);
            refetchStudents();
        } catch (error) {
            console.error('Status update error:', error);
            message.error(`Durum güncellenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const showEditModal = (student) => {
        setEditingStudent(student);
        const rawUrl = student.imageUrl || student.ImageUrl;
        const url = rawUrl ? ensureHttps(rawUrl) : null;
        setFileList(url ? [{ uid: '-1', name: (student.fullName || 'Öğrenci') + '.jpg', status: 'done', url }] : []);
        setStudentImageFile(null);
        
        // Yaş gruplarını name'e göre ID'lere çevir
        const selectedAgeGroupIds = student.ageGroups ? 
            ageGroups
                .filter(ageGroup => student.ageGroups.includes(ageGroup.name))
                .map(ageGroup => ageGroup.id) : [];
        
        // Grupları name'e göre ID'lere çevir
        const selectedGroupIds = student.groups ? 
            groups
                .filter(group => student.groups.includes(group.name))
                .map(group => group.id) : [];
        
        form.setFieldsValue({
            fullName: student.fullName,
            email: student.email || '',
            birthdate: student.birthDate ? dayjs(student.birthDate) : null,
            gender: student.gender,
            phoneNumber: student.phoneNumber || '',
            address: student.address || '',
            height: student.height || null,
            identityNumber: student.identityNumber || '',
            licenseStatus: student.licenseStatus || null,
            licenseNumber: student.licenseNumber || '',
            registrationDate: student.registrationDate ? dayjs(student.registrationDate) : null,
            groupIds: selectedGroupIds,
            ageGroupIds: selectedAgeGroupIds,
            parents: student.parents ? student.parents.map(parent => ({
                ...parent,
                isEmergency: parent.isEmergency || false,
                isSmsChecked: parent.isSmsChecked || false
            })) : []
            // Paket bilgileri düzenleme sırasında gösterilmez
        });
        setShowAddForm(true);
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingStudent(null);
        setShowPackageSection(false);
        form.resetFields();
        setStudentImageFile(null);
        setFileList([]);
        setShowPackageSection(false);
    };

    // Türkçe karakterleri normalize eden fonksiyon
    const normalizeText = (text) => {
        if (!text) return '';
        return text
            .toString()
            // Önce büyük harfleri küçük harfe çevir
            .replace(/İ/g, 'i')
            .replace(/Ğ/g, 'g')
            .replace(/Ü/g, 'u')
            .replace(/Ş/g, 's')
            .replace(/Ö/g, 'o')
            .replace(/Ç/g, 'c')
            .toLowerCase()
            // Sonra tüm Türkçe karakterleri normalize et
            .replace(/ı/g, 'i')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            // Boşlukları temizle
            .replace(/\s+/g, ' ')
            .trim();
    };

    // Filtreleme fonksiyonu
    const filteredStudents = students.filter(student => {
        // Status filtreleme
        const statusMatch = showInactiveStudents ? true : student.status === true;
        
        // İsme göre arama (Türkçe karakter desteği ile)
        const normalizedStudentName = normalizeText(student.fullName);
        const normalizedSearchText = normalizeText(searchText);
        const lowerStudentName = student.fullName.toLowerCase();
        const lowerSearchText = searchText.toLowerCase();
        
        const nameMatch = searchText === '' || 
            normalizedStudentName.includes(normalizedSearchText) ||
            lowerStudentName.includes(lowerSearchText) ||
            normalizedStudentName.includes(lowerSearchText);
        
        // Debug için (geliştirme aşamasında)
        if (searchText && searchText.toLowerCase().includes('hira')) {
            console.log('Debug HİRA search:', {
                studentName: student.fullName,
                normalizedStudentName,
                lowerStudentName,
                searchText,
                normalizedSearchText,
                lowerSearchText,
                nameMatch
            });
        }
        
        // Yaş gruplarına göre filtreleme
        const ageGroupMatch = selectedAgeGroups.length === 0 || 
            (student.ageGroups && 
             student.ageGroups.some(ageGroupName => {
                 const ageGroup = ageGroups.find(ag => ag.name === ageGroupName);
                 return ageGroup && selectedAgeGroups.includes(ageGroup.id);
             }));
        
        // Gruplara göre filtreleme
        const groupMatch = selectedGroups.length === 0 || 
            (student.groups && 
             student.groups.some(groupName => {
                 const group = groups.find(g => g.name === groupName);
                 return group && selectedGroups.includes(group.id);
             }));
        
        return statusMatch && nameMatch && ageGroupMatch && groupMatch;
    });

    // Öğrenci durum sayıları (tüm öğrenciler üzerinden, filtreden bağımsız)
    const activeStudentCount = students.filter((s) => s.status === true).length;
    const passiveStudentCount = students.filter((s) => s.status === false).length;

    // Removed syncing of total to avoid unnecessary state updates; total is computed inline in Table

    const columns = [
        {
            title: '',
            key: 'image',
            width: 60,
            render: (_, record) => {
                const raw = record.imageUrl || record.ImageUrl;
                if (!raw) return null;
                const src = ensureHttps(raw);
                return (
                    <img
                        src={src}
                        alt={record.fullName || 'Öğrenci'}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                );
            }
        },
        {
            title: 'Öğrenci Adı',
            dataIndex: 'fullName',
            key: 'fullName',
            fixed: 'left',
        },
        {
            title: 'Telefon',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            render: (phoneNumber) => {
                if (!phoneNumber) return '-';
                // 10 haneli telefon numarasını formatla: 5XX XXX XX XX
                const formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
                return formatted;
            },
        },
        {
            title: 'Doğum Tarihi',
            dataIndex: 'birthDate',
            key: 'birthDate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Kayıt Tarihi',
            dataIndex: 'registrationDate',
            key: 'registrationDate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Lisans Durumu',
            dataIndex: 'licenseStatus',
            key: 'licenseStatus',
            render: (status) => {
                if (!status || status === 0) return <Tag color="default">Lisans Yok</Tag>;
                if (status === 1) return <Tag color="success">Aktif Lisans</Tag>;
                if (status === 2) return <Tag color="error">Süresi Dolmuş</Tag>;
                return <Tag color="default">-</Tag>;
            },
        },
        {
            title: 'Lisans No',
            dataIndex: 'licenseNumber',
            key: 'licenseNumber',
            render: (value) => value ? <Text>{value}</Text> : <Text type="secondary">-</Text>,
        },
        {
            title: 'Yaş Grupları',
            dataIndex: 'ageGroups',
            key: 'ageGroups',
            render: (ageGroups) => (
                <Space size={[0, 8]} wrap>
                    {ageGroups && ageGroups.map((ageGroup, index) => (
                        <Tag key={index} color="green">{ageGroup}</Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Gruplar',
            dataIndex: 'groups',
            key: 'groups',
            render: (groups) => (
                <Space size={[0, 8]} wrap>
                    {groups && groups.map((group, index) => (
                        <Tag key={index} color="blue">{group}</Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (
                <Switch
                    checked={status}
                    onChange={(checked) => handleStatusChange(record.id, checked)}
                    checkedChildren="Aktif"
                    unCheckedChildren="Pasif"
                />
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    />
                    <Popconfirm
                        title="Bu öğrenciyi silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDeleteStudent(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const visibleColumns = columns.filter(col => visibleColumnKeys.includes(col.key));

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Öğrencileri yönetmek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <Flex vertical gap="middle" style={{ padding: '16px' }}>
            <Flex justify="space-between" align="center">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddForm(true)}
                    loading={loading}
                >
                    Yeni Öğrenci Ekle
                </Button>
                <Popover
                    placement="bottomRight"
                    trigger={['click']}
                    content={renderColumnToggleContent}
                >
                    <Button>Kolonları Seç</Button>
                </Popover>
            </Flex>

            {/* Filtreleme Kısmı */}
            <Card>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Input
                            placeholder="Öğrenci adı ile ara..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            prefix={<SearchOutlined />}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Select
                            mode="multiple"
                            placeholder="Yaş grupları seçin..."
                            value={selectedAgeGroups}
                            onChange={setSelectedAgeGroups}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            {ageGroups.map(ageGroup => (
                                <Option key={ageGroup.id} value={ageGroup.id}>
                                    {ageGroup.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Select
                            mode="multiple"
                            placeholder="Gruplar seçin..."
                            value={selectedGroups}
                            onChange={setSelectedGroups}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            {groups.map(group => (
                                <Option key={group.id} value={group.id}>
                                    {group.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
                <Row style={{ marginTop: 16 }}>
                    <Col span={24}>
                        <Space>
                            <Button 
                                type="default" 
                                onClick={() => {
                                    setSearchText('');
                                    setSelectedAgeGroups([]);
                                    setSelectedGroups([]);
                                }}
                            >
                                Filtreleri Temizle
                            </Button>
                            <Text type="secondary">
                                {filteredStudents.length} öğrenci bulundu
                            </Text>
                            <Tag color="success">Aktif: {activeStudentCount}</Tag>
                            <Tag color="default">Pasif: {passiveStudentCount}</Tag>
                        </Space>
                    </Col>
                </Row>
                <Row style={{ marginTop: 8 }}>
                    <Col span={24}>
                        <Space>
                            <Button 
                                type={showInactiveStudents ? "primary" : "default"}
                                onClick={() => setShowInactiveStudents(!showInactiveStudents)}
                            >
                                {showInactiveStudents ? 'Sadece Aktif Öğrencileri Göster' : 'Bütün Öğrencileri Göster'}
                            </Button>
                            <Text type="secondary" className="active-students-info">
                                {showInactiveStudents ? 'Şu an durumu aktif ve pasif olan bütün öğrenciler gözüküyor' : 'Şu anda sadece aktif öğrenciler gözüküyor'}
                            </Text>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Modal
                title={editingStudent ? "Öğrenci Düzenle" : "Yeni Öğrenci Ekle"}
                open={showAddForm}
                onCancel={handleCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={editingStudent ? handleEditStudent : handleAddStudent}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Öğrenci Resmi">
                                <Upload
                                    listType="picture-card"
                                    fileList={fileList}
                                    onChange={handleUploadChange}
                                    onPreview={handlePreview}
                                    beforeUpload={() => false}
                                    accept="image/*"
                                    maxCount={1}
                                >
                                    {fileList.length >= 1 ? null : (
                                        <div>
                                            <PlusOutlined />
                                            <div style={{ marginTop: 8 }}>Resim Ekle</div>
                                        </div>
                                    )}
                                </Upload>
                                <Text type="secondary">Opsiyonel</Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="fullName"
                                label="Ad Soyad"
                                rules={[{ required: true, message: 'Lütfen ad soyad girin!' }]}
                            >
                                <Input placeholder="Öğrencinin adı ve soyadı" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="birthdate"
                                label="Doğum Tarihi"
                                rules={[{ required: true, message: 'Lütfen doğum tarihi seçin!' }]}
                            >
                                <DatePicker 
                                    style={{ width: '100%' }} 
                                    format="DD/MM/YYYY"
                                    onChange={(date) => {
                                        if (!date || editingStudent) return;
                                        const year = date.year();
                                        // Yaş grubu isimlerinde yıl arayan kod
                                        const matchedAgeGroup = ageGroups.find(ag => ag.name.includes(year.toString()));
                                        if (matchedAgeGroup) {
                                            form.setFieldValue('ageGroupIds', [matchedAgeGroup.id]);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="gender"
                                label="Cinsiyet"
                                rules={[{ required: true, message: 'Lütfen cinsiyet seçin!' }]}
                            >
                                <Select placeholder="Cinsiyet seçin">
                                    <Option value="Male">Erkek</Option>
                                    <Option value="Female">Kadın</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="identityNumber"
                                label="TC Kimlik No"
                                rules={[
                                    { len: 11, message: 'TC kimlik numarası 11 haneli olmalıdır!' }
                                ]}
                            >
                                <Input maxLength={11} placeholder="TC kimlik numarası (opsiyonel)" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="phoneNumber"
                                label="Telefon Numarası"
                            >
                                <InputMask
                                    mask="(999) 999 99 99"
                                    maskChar={null}
                                    alwaysShowMask={false}
                                    beforeMaskedValueChange={(newState) => {
                                        let { value } = newState;
                                        // Başa 0 girilirse sil
                                        if (value.startsWith('(0')) {
                                            value = value.replace('(0', '(');
                                        }
                                        return { value, selection: newState.selection };
                                    }}
                                >
                                    {(inputProps) => <Input {...inputProps} placeholder="(5XX) XXX XX XX" maxLength={15} />}
                                </InputMask>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="email"
                                label="E-posta"
                                rules={[
                                    { type: 'email', message: 'Geçerli bir e-posta girin!' }
                                ]}
                            >
                                <Input placeholder="E-posta adresi (opsiyonel)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="licenseStatus"
                                label="Lisans Durumu"
                            >
                                <Select placeholder="Lisans durumu seçin (opsiyonel)" allowClear>
                                    <Option value={0}>Lisans Yok</Option>
                                    <Option value={1}>Aktif Lisans</Option>
                                    <Option value={2}>Süresi Dolmuş Lisans</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="licenseNumber"
                                label="Lisans Numarası"
                            >
                                <Input placeholder="Lisans numarası (opsiyonel)" maxLength={50} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="registrationDate"
                                label="Kayıt Tarihi"
                                rules={[{ required: true, message: 'Lütfen kayıt tarihi seçin!' }]}
                            >
                                <DatePicker 
                                    style={{ width: '100%' }} 
                                    format="DD/MM/YYYY"
                                    placeholder="Kayıt tarihi"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="height"
                                label="Boy (cm)"
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    min={0} 
                                    max={300}
                                    placeholder="Boy (cm)"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                name="address"
                                label="Adres"
                            >
                                <Input.TextArea rows={3} placeholder="Adres bilgisi (opsiyonel)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="ageGroupIds"
                                label="Yaş Grupları"
                            >
                                <Select mode="multiple" placeholder="Yaş gruplarını seçin (opsiyonel)" allowClear>
                                    {ageGroups.map(ageGroup => (
                                        <Option key={ageGroup.id} value={ageGroup.id}>
                                            {ageGroup.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="groupIds"
                                label="Gruplar"
                            >
                                <Select mode="multiple" placeholder="Grupları seçin (opsiyonel)" allowClear>
                                    {groups.map(group => (
                                        <Option key={group.id} value={group.id}>
                                            {group.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Paket bilgileri sadece yeni öğrenci eklerken gösterilir */}
                    {!editingStudent && (
                        <>
                            <Divider orientation="left">Paket Bilgileri</Divider>
                            <Form.Item>
                                <Button type="dashed" onClick={() => setShowPackageSection(!showPackageSection)} block icon={<PlusOutlined />}>
                                    Paket Ekle
                                </Button>
                            </Form.Item>
                            
                            {showPackageSection && (
                                <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="packagePlanId"
                                                label="Paket Planı"
                                            >
                                                <Select placeholder="Paket planı seçin (opsiyonel)" allowClear>
                                                    {packagePlans.map(packagePlan => (
                                                        <Option key={packagePlan.id} value={packagePlan.id}>
                                                            {packagePlan.name} - {packagePlan.price} TL
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="packagePrice"
                                                label="Paket Fiyatı"
                                                extra="Boş bırakılırsa plan fiyatı uygulanır"
                                            >
                                                <InputNumber 
                                                    style={{ width: '100%' }} 
                                                    min={0} 
                                                    placeholder="Paket fiyatı"
                                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="paidAmount"
                                                label="Ödenen Miktar"
                                            >
                                                <InputNumber 
                                                    style={{ width: '100%' }} 
                                                    min={0} 
                                                    placeholder="Ödenen miktar (opsiyonel)"
                                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="packageNote"
                                                label="Paket Notu"
                                            >
                                                <Input placeholder="Paket ile ilgili not" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="packageStartTime"
                                                label="Başlangıç Tarihi"
                                            >
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    placeholder="Başlangıç tarihi"
                                                    format="DD/MM/YYYY"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="packageEndTime"
                                                label="Bitiş Tarihi"
                                            >
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    placeholder="Bitiş tarihi"
                                                    format="DD/MM/YYYY"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* Hızlı Tarih Seçimi Butonları */}
                                    <Form.Item label="Hızlı Tarih Seçimi">
                                        <Space wrap>
                                            <Button size="small" onClick={() => setQuickDateRange('thisMonth')}>
                                                Bu Ay
                                            </Button>
                                            <Button size="small" onClick={() => setQuickDateRange('oneMonth')}>
                                                1 Ay
                                            </Button>
                                            <Button size="small" onClick={() => setQuickDateRange('threeMonths')}>
                                                3 Ay
                                            </Button>
                                            <Button size="small" onClick={() => setQuickDateRange('sixMonths')}>
                                                6 Ay
                                            </Button>
                                            <Button size="small" onClick={() => setQuickDateRange('oneYear')}>
                                                1 Yıl
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </div>
                            )}
                        </>
                    )}

                    {/* Parent bilgileri */}
                    <Divider orientation="left">Anne/Baba Bilgileri</Divider>
                    <Form.List name="parents">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <div key={field.key} style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                                        <Row gutter={16} align="middle">
                                            <Col span={5}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'fullName']}
                                                    fieldKey={[field.fieldKey, 'fullName']}
                                                    label={`${index + 1}. Anne/Baba Adı`}
                                                    rules={[{ required: true, message: `Lütfen ${index + 1}. anne/baba adını girin!` }]}
                                                >
                                                    <Input placeholder="Ad soyad" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'email']}
                                                    fieldKey={[field.fieldKey, 'email']}
                                                    label="E-posta"
                                                    rules={[
                                                        { type: 'email', message: 'Geçerli bir e-posta girin!' }
                                                    ]}
                                                >
                                                    <Input placeholder="E-posta" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'phoneNumber']}
                                                    fieldKey={[field.fieldKey, 'phoneNumber']}
                                                    label="Telefon"
                                                >
                                                    <InputMask
                                                        mask="(999) 999 99 99"
                                                        maskChar={null}
                                                        alwaysShowMask={false}
                                                        beforeMaskedValueChange={(newState) => {
                                                            let { value } = newState;
                                                            if (value.startsWith('(0')) {
                                                                value = value.replace('(0', '(');
                                                            }
                                                            return { value, selection: newState.selection };
                                                        }}
                                                    >
                                                        {(inputProps) => <Input {...inputProps} placeholder="(5XX) XXX XX XX" maxLength={15} />}
                                                    </InputMask>
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'address']}
                                                    fieldKey={[field.fieldKey, 'address']}
                                                    label="Adres"
                                                >
                                                    <TextArea rows={2} placeholder="Adres" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={1}>
                                                <MinusCircleOutlined 
                                                    onClick={() => remove(field.name)} 
                                                    style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '16px' }}
                                                />
                                            </Col>
                                        </Row>
                                        <Row gutter={16} style={{ marginTop: 8 }}>
                                            <Col span={8}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'isEmergency']}
                                                    fieldKey={[field.fieldKey, 'isEmergency']}
                                                    valuePropName="checked"
                                                >
                                                    <Checkbox>Acil Durum İletişimi</Checkbox>
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'isSmsChecked']}
                                                    fieldKey={[field.fieldKey, 'isSmsChecked']}
                                                    valuePropName="checked"
                                                >
                                                    <Checkbox>SMS İzni</Checkbox>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Yeni Anne/Baba Ekle
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingStudent ? 'Güncelle' : 'Kaydet'}
                            </Button>
                            <Button onClick={handleCancel}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
                <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
                    <img alt="Öğrenci" style={{ width: '100%' }} src={previewImage} />
                </Modal>
            </Modal>

            <Table
                columns={visibleColumns}
                dataSource={filteredStudents}
                rowKey="id"
                pagination={{
                    ...pagination,
                    total: filteredStudents.length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} öğrenci`
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
                loading={loading}
                locale={{
                    emptyText: loading ? 'Yükleniyor...' : 'Henüz öğrenci bulunmamaktadır'
                }}
            />
        </Flex>
    );
};

export default Students; 